import { useState, useEffect, useMemo, useRef } from 'react';
import Fuse from 'fuse.js';
import { getAllBrandProducts } from '../../../../api/allbrands';

const SIMILARITY_THRESHOLD = 0.3; // Lower is more similar (0-1 scale)
const DEBOUNCE_DELAY = 300; // ms

/**
 * Hook to find similar product titles from the same brand
 * @param {Object} params - Hook parameters
 * @param {number|null} params.brandId - The ID of the selected brand
 * @param {string} params.title - The current product title being entered
 * @param {number|null} params.currentProductId - ID of current product (for edit mode, to exclude from results)
 * @returns {Object} { similarProducts, loading, error }
 */
export const useBrandProductSimilarity = ({ brandId, title, currentProductId = null }) => {
  const [brandProducts, setBrandProducts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceTimer = useRef(null);
  const [debouncedTitle, setDebouncedTitle] = useState(title);

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedTitle(title);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [title]);

  // Fetch products when brand changes
  useEffect(() => {
    if (!brandId) {
      return;
    }

    if (brandProducts[brandId]) {
      return;
    }

    const abortController = new AbortController();

    const fetchProducts = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await getAllBrandProducts(brandId, {
          signal: abortController.signal
        });
        setBrandProducts(prev => ({
          ...prev,
          [brandId]: response.products || []
        }));
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error fetching brand products:', err);
          setError('Failed to fetch brand products');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();

    return () => abortController.abort();
  }, [brandId, brandProducts]);

  const similarProducts = useMemo(() => {
    if (!brandId || !debouncedTitle || !brandProducts[brandId] || debouncedTitle.length < 3) {
      return [];
    }

    const productsToSearch = currentProductId ? brandProducts[brandId].filter(p => p.id !== currentProductId) : brandProducts[brandId];

    if (productsToSearch.length === 0) {
      return [];
    }

    const fuse = new Fuse(productsToSearch, {
      keys: ['title'],
      threshold: SIMILARITY_THRESHOLD,
      includeScore: true,
      ignoreLocation: false,
      minMatchCharLength: 3
    });

    const results = fuse.search(debouncedTitle);

    return results
      .map(result => ({
        ...result.item,
        similarity: Math.round((1 - result.score) * 100)
      }))
      .filter(item => item.similarity >= 70); // Only show items with 70%+ similarity
  }, [brandId, debouncedTitle, brandProducts, currentProductId]);

  return {
    similarProducts,
    loading,
    error
  };
};
